import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ExecKpiCard } from "@/types/executive";

const TREND_COLOR: Record<ExecKpiCard["trend"], string> = {
  good: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

/** The subset of ExecKpiCard this card renders. The legacy /dashboard feed
 *  (useMetrics.KpiCard) satisfies it once is_proxy is mapped to isProxy. */
export type KpiValueCardData = Pick<
  ExecKpiCard,
  "id" | "label" | "value" | "unit" | "delta" | "trend"
> & { isProxy?: boolean };

interface KpiValueCardProps {
  kpi: KpiValueCardData;
  tooltip?: string;
}

export function KpiValueCard({ kpi, tooltip }: KpiValueCardProps) {
  const { t } = useTranslation();
  const displayValue = kpi.value === null ? "–" : Number(kpi.value.toFixed(1));
  const showDelta = kpi.delta !== null && kpi.trend !== "neutral";

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex min-w-0 items-center gap-2">
          <CardTitle className="truncate text-sm font-medium" title={tooltip}>{kpi.label}</CardTitle>
          {kpi.isProxy && (
            <span
              className="shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground"
              title={t("dashboard.proxyHint")}
            >
              {t("dashboard.proxyBadge")}
            </span>
          )}
        </div>
        <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">{kpi.unit}</span>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{displayValue}</div>
        {showDelta && (
          <p className={`text-xs ${TREND_COLOR[kpi.trend]}`}>
            {kpi.delta! < 0 ? "↓" : "↑"} {Math.abs(kpi.delta!).toFixed(1)}{" "}
            {t("dashboard.vsPrevPeriod")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
